import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const FREE_PLAY_LIMIT = 10;

// Server-enforced gate: anonymous users get a limited number of plays, then must log in.
export function usePlayGate() {
  const { user } = useAuth();
  const [blocked, setBlocked] = useState(false);
  const [playsLeft, setPlaysLeft] = useState(FREE_PLAY_LIMIT);
  const fetched = useRef(false);

  useEffect(() => {
    if (user) { setBlocked(false); setPlaysLeft(Infinity); return; }
    if (user === false) {
      api.get("/plays/status")
        .then(({ data }) => {
          setPlaysLeft(data.plays_left ?? FREE_PLAY_LIMIT);
          setBlocked(!data.allowed);
        })
        .catch(() => {});
    }
  }, [user]);

  // call when a new game session starts; returns a promise<boolean>
  const registerPlay = useCallback(async () => {
    if (user) return true; // logged in => unlimited
    try {
      const { data } = await api.post("/plays/register");
      setPlaysLeft(data.plays_left ?? 0);
      setBlocked(!data.allowed);
      return !!data.allowed;
    } catch {
      return true; // fail-open so a transient error never blocks a player
    }
  }, [user]);

  return { blocked, playsLeft, registerPlay, limit: FREE_PLAY_LIMIT, isAuthed: !!user };
}
